import * as React from 'react';
import {
  type FieldPath,
  type FieldValues,
  useFormContext,
} from 'react-hook-form';

import {
  MultiSelect,
  type MultiSelectProps,
  type MultiSelectRef,
} from '@/modules/ui/MultiSelect';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './Forms';

interface FormMultiSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<MultiSelectProps, 'onValueChange' | 'defaultValue' | 'name'> {
  name: TName;
  label: string;
  description?: string;
  required?: boolean;
}

export const FormMultiSelect = React.forwardRef<
  MultiSelectRef,
  FormMultiSelectProps
>(function FormMultiSelect<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  {
    name,
    label,
    description,
    required,
    disabled,
    ...props
  }: FormMultiSelectProps<TFieldValues, TName>,
  ref: React.ForwardedRef<MultiSelectRef>,
) {
  const { control } = useFormContext<TFieldValues>();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const value = Array.isArray(field.value) ? field.value : [];

        return (
          <FormItem>
            <FormLabel required={required}>{label}</FormLabel>
            <FormControl>
              <div>
                <MultiSelect
                  ref={ref}
                  {...props}
                  disabled={disabled}
                  defaultValue={value}
                  onValueChange={field.onChange}
                  resetOnDefaultValueChange={true}
                />
              </div>
            </FormControl>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}) as <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: FormMultiSelectProps<TFieldValues, TName> & {
    ref?: React.ForwardedRef<MultiSelectRef>;
  },
) => React.ReactElement;
